import { DOCUMENT } from '@angular/common';
import {
  Directive,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  Renderer2,
  RendererStyleFlags2,
  SimpleChanges,
} from '@angular/core';
import { User, UserRole } from 'src/types/graphql';

@Directive({
  selector: '[avatar]',
  standalone: true,
})
export class AvatarDirective implements OnChanges {
  @Input() avatar: {
    size: 'small' | 'big';
    imgSize: string;
    user?: Partial<User>;
    imgValueUrl?: string;
    noBorder?: boolean;
  } = { size: 'small', imgSize: '48px' };

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!!changes && !!changes.avatar && !!changes.avatar.currentValue) {
      if (this.el.nativeElement.firstElementChild) {
        this.thumbaTutto(this.el.nativeElement.firstElementChild);
        if (!!this.avatar.user?.image?.url || this.avatar.imgValueUrl) {
          this.renderer.setStyle(
            this.el.nativeElement.firstElementChild,
            'background-image',
            `url(${this.avatar.imgValueUrl || this.avatar.user.image.url})`,
          );
          if (this.el.nativeElement.firstElementChild.firstElementChild) {
            this.renderer.removeChild(
              this.el.nativeElement.firstElementChild,
              this.el.nativeElement.firstElementChild.firstElementChild,
            );
          }
        }
      }
      if (!this.el.nativeElement.firstElementChild) {
        const innerElement = this.document.createElement('span');
        this.renderer.setStyle(
          innerElement,
          'background-image',
          `url(https://media.sharry.land/app/images/profile-placeholder.webp)`,
        );
        this.renderer.setStyle(innerElement, 'display', `block`);
        this.thumbaTutto(innerElement);
        this.renderer.setStyle(innerElement, 'border', `${this.avatar.size === 'small' ? '1px' : '3px'} solid #fff`);
        if (!!this.avatar.user?.image?.url || this.avatar.imgValueUrl) {
          this.renderer.setStyle(
            innerElement,
            'background-image',
            `url(${this.avatar.imgValueUrl || this.avatar.user.image.url})`,
          );
        } else if (!this.avatar.user?.id) {
          this.renderer.setStyle(this.el.nativeElement, 'pointer-events', 'none');
        } else {
          this.renderer.removeStyle(innerElement, 'background-image');
          this.renderer.setStyle(innerElement, 'background-color', `#7e7e7e`);
          this.renderer.setStyle(innerElement, 'display', `flex`);
          if (!innerElement.firstElementChild) {
            if (!this.avatar.user?.anonymised) {
              const span = this.document.createElement('span');
              this.renderer.setStyle(span, 'margin', `auto`);
              this.renderer.setStyle(span, 'text-transform', `uppercase`);
              this.renderer.setStyle(span, 'color', `#fff`);
              this.renderer.setStyle(span, 'font-weight', `600`);
              this.renderer.setStyle(span, 'font-size', `${Number(this.avatar.imgSize.replace('px', '')) / 3}px`);
              span.innerText = this.getUserInitials(this.avatar.user);
              this.renderer.appendChild(innerElement, span);
            }
          }
        }
        this.renderer.appendChild(this.el.nativeElement, innerElement);
      }
      Object.entries(this.square(this.avatar.imgSize)).forEach(entry => {
        this.renderer.setStyle(this.el.nativeElement, entry[0], entry[1]);
      });
      this.renderer.setStyle(this.el.nativeElement, 'background-color', '#fff');
      this.renderer.setStyle(this.el.nativeElement, 'display', 'flex');
      this.renderer.setStyle(this.el.nativeElement, 'align-items', 'center');
      this.renderer.setStyle(this.el.nativeElement, 'justify-content', 'center');
      this.renderer.setStyle(this.el.nativeElement, 'border-radius', '50%');
      this.renderer.setStyle(
        this.el.nativeElement,
        'border-width',
        this.avatar.size === 'small' ? '1px' : '2px',
        RendererStyleFlags2.Important,
      );
      this.renderer.setStyle(this.el.nativeElement, 'border-style', 'solid', RendererStyleFlags2.Important);
      if (!this.avatar.noBorder) {
        this.renderer.setStyle(
          this.el.nativeElement,
          'border-color',
          this.avatar.user?.sharrylandPartner
            ? '#c79315'
            : this.avatar.user?.role === UserRole.TrustedUser
              ? '#dd407f'
              : '#7e7e7e',
          RendererStyleFlags2.Important,
        );
      } else {
        this.renderer.setStyle(this.el.nativeElement, 'border-color', '#fff', RendererStyleFlags2.Important);
      }
    }
  }
  private square($size: string) {
    return {
      width: $size,
      ['min-width']: $size,
      ['max-width']: $size,
      height: $size,
      ['min-height']: $size,
      ['max-height']: $size,
    };
  }
  private thumbaTutto(el: any) {
    Object.entries(this.thumbnail(`${Number(this.avatar.imgSize.replace('px', '')) - 4}px`)).forEach(entry => {
      this.renderer.setStyle(el, entry[0], entry[1]);
    });
  }
  private thumbnail($size: string) {
    return {
      ...this.square($size),
      ['background-size']: 'cover',
      ['background-position']: 'center',
      ['border-radius']: '50%',
      ['background-repeat']: 'no-repeat',
    };
  }
  private getUserInitials(user: Partial<User>) {
    if (!user) return '';
    if (user.anonymised) {
      return '';
    }
    return user.isSmallOrganisation
      ? user.businessName.substring(0, 2)
      : (user.name || 'u')[0] + (user.surname || 'p')[0];
  }
}
